import { useState } from "react";
import { toast } from "sonner";
import { Plus, X, Sparkles, Activity, Focus, HandHelping, Palette, Moon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useCreateCustomHabit } from "@/lib/hooks";

const CATEGORIES = [
  { id: "mind",  label: "Mindfulness", icon: Sparkles },
  { id: "body",  label: "Body",        icon: Activity },
  { id: "focus", label: "Focus",       icon: Focus },
  { id: "faith", label: "Faith",       icon: HandHelping },
  { id: "craft", label: "Craft",       icon: Palette },
  { id: "rest",  label: "Rest",        icon: Moon },
];

export default function CreateHabitDialog({ open, onOpenChange }) {
  const create = useCreateCustomHabit();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "focus",
    duration_min: 30,
    default_start: "08:00",
  });

  const reset = () => setForm({
    title: "", description: "", category: "focus", duration_min: 30, default_start: "08:00",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Give your habit a name.");
      return;
    }
    try {
      const res = await create.mutateAsync({
        ...form,
        duration_min: Number(form.duration_min),
      });
      toast("Your habit is on the calendar.", {
        description: `${res.template.title} · ${res.template.durationMin} min at ${res.template.defaultStart}`,
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error("Couldn't create the habit", { description: detail });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="create-habit-dialog">
        <DialogHeader>
          <span className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" /> Premium · custom habit
          </span>
          <DialogTitle className="font-serif text-3xl leading-tight text-stone-900">
            Start a new habit of your own.
          </DialogTitle>
          <DialogDescription className="text-sm text-stone-500">
            Only you and your circle will see it on the shared calendar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="create-habit-form">
          <div>
            <label className="eyebrow block mb-1.5">Habit name</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={60}
              placeholder="Long walk before lunch"
              className="w-full bg-transparent border-b-2 border-stone-300 focus:border-terracotta outline-none py-2 text-sm"
              data-testid="create-habit-title"
              autoFocus
            />
          </div>

          <div>
            <label className="eyebrow block mb-1.5">A quiet description (optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              maxLength={200}
              placeholder="No podcasts. Just steps."
              className="w-full bg-transparent border-2 border-stone-200 rounded-2xl focus:border-terracotta outline-none p-3 text-sm resize-none"
              data-testid="create-habit-description"
            />
          </div>

          <div>
            <label className="eyebrow block mb-1.5">Category</label>
            <div className="flex flex-wrap gap-2" data-testid="create-habit-category-group">
              {CATEGORIES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, category: id }))}
                  data-testid={`create-habit-cat-${id}`}
                  className={`tag-pill inline-flex items-center gap-1.5 ${
                    form.category === id ? "bg-charcoal text-paper border-charcoal" : ""
                  }`}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-1.5">Duration (min)</label>
              <input
                type="number"
                min={5}
                max={240}
                step={5}
                value={form.duration_min}
                onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))}
                className="w-full bg-transparent border-b-2 border-stone-300 focus:border-terracotta outline-none py-2 text-sm data-mono"
                data-testid="create-habit-duration"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Default start</label>
              <input
                type="time"
                value={form.default_start}
                onChange={(e) => setForm((f) => ({ ...f, default_start: e.target.value }))}
                className="w-full bg-transparent border-b-2 border-stone-300 focus:border-terracotta outline-none py-2 text-sm data-mono"
                data-testid="create-habit-start"
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline"
              data-testid="create-habit-cancel"
            >
              <X size={14} /> Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="btn-accent"
              data-testid="create-habit-submit"
            >
              <Plus size={14} /> {create.isPending ? "Creating…" : "Add to my rhythm"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
